import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip, // 💡 Importamos el componente Chip
} from '@mui/material';
import api from '../api/api';

const BuscarPacienteModal = ({ open, handleClose, navigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sucursales, setSucursales] = useState({});
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const fetchSucursales = async () => {
        try {
          const uniqueSucursalIds = [...new Set(pacientes.map(p => p.sucursal_id || p.id_sucursal))];
          if (uniqueSucursalIds.length > 0) {
            const promises = uniqueSucursalIds.map(id =>
              api.get(`/sucursales/${id}`).then(res => ({ id, nombre: res.data.nombre_sucursal }))
            );
            const results = await Promise.all(promises);
            const sucursalMap = results.reduce((acc, { id, nombre }) => {
              acc[id] = nombre || `Sucursal ${id}`;
              return acc;
            }, {});
            setSucursales(sucursalMap);
          }
        } catch (err) {
          console.error('Error al cargar sucursales:', err);
        }
      };
      fetchSucursales();
    }
  }, [open, pacientes]);

  const handleSearch = () => {
    if (searchTerm.trim().length >= 2) {
      setLoading(true);
      setError(null);
      api.get('/pacientes/buscar', { params: { terminos_busqueda: searchTerm } })
        .then(response => {
          console.log('Pacientes encontrados:', response.data);
          setPacientes(response.data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error al buscar pacientes:', err);
          setError('Error al buscar pacientes');
          setLoading(false);
          setPacientes([]);
        });
    } else {
      setPacientes([]);
      setError('Escribe al menos 2 caracteres para buscar.');
    }
  };

  const handleSelectPaciente = (idCliente) => {
    if (navigate) {
      navigate(`/clientes/${idCliente}`);
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Buscar Paciente</DialogTitle>
      <DialogContent>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            label="Buscar por nombre de cliente, paciente o Qr paciente"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputRef={inputRef}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            disabled={loading}
            required
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searchTerm.trim().length < 2 || loading}
          >
            Buscar
          </Button>
        </Box>

        {loading && <p>Cargando...</p>}
        {!loading && pacientes.length > 0 && (
          <List>
            {pacientes.map((paciente) => {
              const sucursalId = paciente.sucursal_id || paciente.id_sucursal;
              const sucursalNombre = sucursales[sucursalId] || 'Desconocida';
              const razaInfo = paciente.nombre_raza ? ` (${paciente.nombre_raza})` : '';

              return (
                <ListItem
                  key={paciente.id_paciente}
                  component="button"
                  onClick={() => handleSelectPaciente(paciente.id_cliente)}
                >
                  <ListItemText
                    primary={
                      <>
                        {paciente.nombre_cliente} - 
                        <Chip
                          label={paciente.nombre_paciente + razaInfo}
                          color="primary"
                          sx={{ ml: 1, fontWeight: 'bold' }}
                        />
                      </>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        {`Sucursal: ${sucursalNombre}${paciente.telefonos_cliente ? ' | Teléfono: ' + paciente.telefonos_cliente : ''}`}
                        
                        {/* Mostramos el QR si existe, resaltándolo para confirmar la coincidencia */}
                        {paciente.qr_code && (
                          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                            <Chip 
                              label={`QR: ${paciente.qr_code}`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ fontSize: '0.75rem', height: '20px' }}
                            />
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
        {!loading && pacientes.length === 0 && searchTerm.trim().length >= 2 && (
          <p>No se encontraron pacientes</p>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BuscarPacienteModal;